import React, { useState, useEffect } from 'react';
import { Plus, Package, Loader2, X, Pencil, Wrench, AlertTriangle, TrendingDown, Calendar, History, Trash2, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { assetService } from '../../services/assetService';
import { employeeService } from '../../services/employeeService';
import { maintenanceRequestService } from '../../services/maintenanceRequestService';
import { newAssetRequestService } from '../../services/newAssetRequestService';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import StatusBadge from '../../components/StatusBadge';

const CATEGORIES = ['Laptop', 'Monitor', 'Phone', 'Peripheral', 'Furniture', 'Networking', 'Other'];
const CONDITIONS = ['New', 'Good', 'Fair', 'Damaged'];
const STATUSES = ['Available', 'Assigned', 'Under Maintenance', 'Disposed'];
const DISPOSAL_REASONS = ['Sold', 'Lost', 'Damaged', 'Burned', 'Other'];

// ===== New/Edit Asset Modal (Original Logic Retained) =====
const AssetModal = ({ editingAsset, onClose, onDone }) => {
  const isEdit = !!editingAsset;
  const [name, setName] = useState(isEdit ? editingAsset.name : '');
  const [category, setCategory] = useState(isEdit ? editingAsset.category : 'Laptop');
  const [make, setMake] = useState(isEdit ? editingAsset.make : '');
  const [model, setModel] = useState(isEdit ? editingAsset.model : '');
  const [serialNumber, setSerialNumber] = useState(isEdit ? editingAsset.serial_number : '');
  const [purchaseDate, setPurchaseDate] = useState(isEdit ? editingAsset.purchase_date : '');
  const [purchaseCost, setPurchaseCost] = useState(isEdit ? editingAsset.purchase_cost : '');
  const [vendor, setVendor] = useState(isEdit ? editingAsset.vendor : '');
  const [warrantyExpiry, setWarrantyExpiry] = useState(isEdit ? (editingAsset.warranty_expiry_date || '') : '');
  const [condition, setCondition] = useState(isEdit ? editingAsset.condition : 'New');
  const [notes, setNotes] = useState(isEdit ? (editingAsset.notes || '') : '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name || !make || !model || !serialNumber || !purchaseDate || !purchaseCost || !vendor) {
      setError('Please fill in all required fields.');
      return;
    }
    const payload = {
      name, category, make, model,
      serial_number: serialNumber,
      purchase_date: purchaseDate,
      purchase_cost: parseFloat(purchaseCost),
      vendor,
      warranty_expiry_date: warrantyExpiry || null,
      condition,
      notes: notes || null
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await assetService.updateAsset(editingAsset.asset_id, payload);
      } else {
        await assetService.createAsset(payload);
      }
      onDone();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <h2 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit Asset' : 'New Asset'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="px-4 py-2.5 rounded-control bg-red-50 text-red-700 text-sm">{error}</div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Name *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Make *</label>
              <input type="text" value={make} onChange={(e) => setMake(e.target.value)}
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Model *</label>
              <input type="text" value={model} onChange={(e) => setModel(e.target.value)}
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Serial Number *</label>
              <input type="text" value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)}
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Condition</label>
              <select value={condition} onChange={(e) => setCondition(e.target.value)}
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light">
                {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Purchase Date *</label>
              <input type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} max="2099-12-31"
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Purchase Cost *</label>
              <input type="number" value={purchaseCost} onChange={(e) => setPurchaseCost(e.target.value)}
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Warranty Expiry</label>
              <input type="date" value={warrantyExpiry} onChange={(e) => setWarrantyExpiry(e.target.value)} max="2099-12-31"
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Vendor *</label>
            <input type="text" value={vendor} onChange={(e) => setVendor(e.target.value)}
              className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
          </div>

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 rounded-control text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={submitting}
              className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark text-white px-4 py-2.5 rounded-control text-sm font-medium disabled:opacity-60">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEdit ? 'Save Changes' : 'Register Asset'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===== Assign Modal (Original Logic Retained) =====
const AssignModal = ({ asset, onClose, onDone }) => {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [assignmentDate, setAssignmentDate] = useState(new Date().toISOString().split('T')[0]);
  const [expectedReturn, setExpectedReturn] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await employeeService.getEmployees(undefined, 'active');
        setEmployees(res.data || []);
      } catch (err) {
        setError(err.message);
      }
    };
    load();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!employeeId || !assignmentDate) {
      setError('Please select an employee and assignment date.');
      return;
    }
    setSubmitting(true);
    try {
      await assetService.assignAsset(asset.asset_id, {
        employee_id: employeeId,
        assignment_date: assignmentDate,
        expected_return_date: expectedReturn || null,
        notes: notes || null
      });
      onDone();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Assign Asset</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 pt-3 pb-2 bg-surface-subtle border-b border-gray-100">
          <p className="text-sm font-medium text-gray-800">{asset.name}</p>
          <p className="text-xs text-gray-500">{asset.asset_id} &middot; {asset.category}</p>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="px-4 py-2.5 rounded-control bg-red-50 text-red-700 text-sm">{error}</div>}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Employee *</label>
            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
              className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light">
              <option value="">Select employee</option>
              {employees.map((emp) => (
                <option key={emp.employee_id} value={emp.employee_id}>
                  {emp.full_name + ' (' + emp.employee_id + ')'}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Assignment Date *</label>
              <input type="date" value={assignmentDate} onChange={(e) => setAssignmentDate(e.target.value)} max="2099-12-31"
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Expected Return</label>
              <input type="date" value={expectedReturn} onChange={(e) => setExpectedReturn(e.target.value)} max="2099-12-31"
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
          </div>
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 rounded-control text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={submitting}
              className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark text-white px-4 py-2.5 rounded-control text-sm font-medium disabled:opacity-60">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Assign
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===== Return Modal (Original Logic Retained) =====
const ReturnModal = ({ asset, onClose, onDone }) => {
  const [returnDate, setReturnDate] = useState(new Date().toISOString().split('T')[0]);
  const [condition, setCondition] = useState('Good');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await assetService.returnAsset(asset.asset_id, {
        return_date: returnDate,
        condition_on_return: condition,
        notes: notes || null
      });
      onDone();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Return Asset</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 pt-3 pb-2 bg-surface-subtle border-b border-gray-100">
          <p className="text-sm font-medium text-gray-800">{asset.name}</p>
          <p className="text-xs text-gray-500">{asset.assigned_to_name || asset.assigned_to}</p>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="px-4 py-2.5 rounded-control bg-red-50 text-red-700 text-sm">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Return Date</label>
              <input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} max="2099-12-31"
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Condition on Return</label>
              <select value={condition} onChange={(e) => setCondition(e.target.value)}
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light">
                {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
          </div>
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 rounded-control text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={submitting}
              className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark text-white px-4 py-2.5 rounded-control text-sm font-medium disabled:opacity-60">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Confirm Return
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===== Dispose Asset Modal =====
const DisposeModal = ({ asset, onClose, onDone }) => {
  const [reason, setReason] = useState('Sold');
  const [customReason, setCustomReason] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const finalReason = reason === 'Other' ? customReason.trim() : reason;
    if (!finalReason) {
      setError('Please provide a disposal reason.');
      return;
    }
    setSubmitting(true);
    try {
      await assetService.disposeAsset(asset.asset_id, {
        reason: finalReason,
        notes: notes || null
      });
      onDone();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Delete / Dispose Asset</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 pt-3 pb-2 bg-surface-subtle border-b border-gray-100">
          <p className="text-sm font-medium text-gray-800">{asset.name}</p>
          <p className="text-xs text-gray-500">{asset.asset_id}</p>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="px-4 py-2.5 rounded-control bg-red-50 text-red-700 text-sm">{error}</div>}
          <p className="text-xs text-gray-500">
            This will archive the asset (soft-delete). It will be removed from active operations but kept in history.
          </p>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Disposal Reason *</label>
            <select value={reason} onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light">
              {DISPOSAL_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          {reason === 'Other' && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Specify Reason *</label>
              <input type="text" value={customReason} onChange={(e) => setCustomReason(e.target.value)}
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Notes (optional)</label>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
          </div>
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 rounded-control text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={submitting}
              className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-control text-sm font-medium disabled:opacity-60">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Delete Asset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===== Maintenance Modal (Original Logic Retained — Admin direct log) =====
const MaintenanceModal = ({ asset, onClose, onDone }) => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [issue, setIssue] = useState('');
  const [cost, setCost] = useState('');
  const [vendor, setVendor] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!issue || !cost || !vendor) {
      setError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      await assetService.logMaintenance(asset.asset_id, {
        date, issue,
        cost: parseFloat(cost),
        vendor,
        notes: notes || null
      });
      onDone();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Log Maintenance</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 pt-3 pb-2 bg-surface-subtle border-b border-gray-100">
          <p className="text-sm font-medium text-gray-800">{asset.name}</p>
          <p className="text-xs text-gray-500">{asset.asset_id}</p>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="px-4 py-2.5 rounded-control bg-red-50 text-red-700 text-sm">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} max="2099-12-31"
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Cost *</label>
              <input type="number" value={cost} onChange={(e) => setCost(e.target.value)}
                className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Issue *</label>
            <input type="text" value={issue} onChange={(e) => setIssue(e.target.value)}
              className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Vendor *</label>
            <input type="text" value={vendor} onChange={(e) => setVendor(e.target.value)}
              className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
          </div>
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 rounded-control text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={submitting}
              className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark text-white px-4 py-2.5 rounded-control text-sm font-medium disabled:opacity-60">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Log Maintenance
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===== Employee Maintenance Request Modal — Reason + Estimated Expense, sent for Admin approval =====
const MaintenanceRequestModal = ({ asset, assets, onClose, onDone }) => {
  const eligibleAssets = (assets || []).filter(a => a.status !== 'Under Maintenance' && a.status !== 'Disposed');
  const [assetId, setAssetId] = useState(asset ? asset.asset_id : (eligibleAssets[0]?.asset_id || ''));
  const [reason, setReason] = useState('');
  const [estimatedCost, setEstimatedCost] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!assetId) return setError('Please select an asset.');
    if (!reason.trim()) return setError('Please describe the reason for this maintenance request.');
    if (!estimatedCost || Number(estimatedCost) <= 0) return setError('Please enter the estimated expense.');

    setSubmitting(true);
    try {
      await maintenanceRequestService.create({
        asset_id: assetId,
        reason: reason.trim(),
        estimated_cost: Number(estimatedCost),
      });
      onDone();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Request Maintenance</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="px-4 py-2.5 rounded-control bg-red-50 text-red-700 text-sm">{error}</div>}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Asset *</label>
            <select value={assetId} onChange={(e) => setAssetId(e.target.value)}
              className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light">
              <option value="">Select asset</option>
              {eligibleAssets.map((a) => (
                <option key={a.asset_id} value={a.asset_id}>{a.name} — {a.asset_id}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Reason *</label>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Screen not turning on"
              className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Estimated Expense (PKR) *</label>
            <input type="number" step="0.01" value={estimatedCost} onChange={(e) => setEstimatedCost(e.target.value)}
              placeholder="e.g. 3000"
              className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
          </div>
          <p className="text-xs text-gray-400">Your request will be sent to admin for approval before any action is taken.</p>
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 rounded-control text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={submitting}
              className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark text-white px-4 py-2.5 rounded-control text-sm font-medium disabled:opacity-60">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Submit Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===== Admin panel: pending maintenance requests, with full detail + Approve/Reject =====
const AdminMaintenanceRequests = ({ onActionDone }) => {
  const { confirmDialog } = useNotification();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await maintenanceRequestService.getAll('Pending');
      setRequests(res.data || []);
    } catch (err) {
      // silent — this panel is non-critical, don't block the page
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDecision = async (requestId, decision) => {
    const confirmed = await confirmDialog(
      decision === 'Approved' ? 'Approve this maintenance request? The asset will be moved to Under Maintenance.' : 'Reject this maintenance request?',
      { title: decision === 'Approved' ? 'Approve Request' : 'Reject Request', confirmLabel: decision === 'Approved' ? 'Approve' : 'Reject', danger: decision === 'Rejected' }
    );
    if (!confirmed) return;
    setActioningId(requestId);
    try {
      if (decision === 'Approved') {
        await maintenanceRequestService.approve(requestId);
      } else {
        await maintenanceRequestService.reject(requestId);
      }
      await load();
      onActionDone && onActionDone();
    } catch (err) {
      alert(err.message);
    } finally {
      setActioningId('');
    }
  };

  if (loading || requests.length === 0) return null;

  return (
    <div className="bg-white rounded-card shadow-soft border border-amber-100 overflow-hidden">
      <div className="px-5 py-4 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
        <Clock className="w-4 h-4 text-amber-600" />
        <h2 className="text-sm font-bold text-amber-800">Pending Maintenance Requests ({requests.length})</h2>
      </div>
      <div className="divide-y divide-gray-50">
        {requests.map((r) => (
          <div key={r.request_id} className="px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm font-bold text-gray-900">{r.asset_name} <span className="text-xs text-gray-400 font-mono">({r.asset_id})</span></p>
              <p className="text-xs text-gray-500 mt-0.5">Requested by {r.employee_name}</p>
              <p className="text-xs text-gray-600 mt-1">{r.reason}</p>
              <p className="text-xs font-bold text-primary mt-1">Estimated: PKR {r.estimated_cost.toLocaleString()}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => handleDecision(r.request_id, 'Approved')} disabled={actioningId === r.request_id}
                className="flex items-center gap-1 bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-50">
                <CheckCircle2 className="w-3.5 h-3.5" /> Approve
              </button>
              <button onClick={() => handleDecision(r.request_id, 'Rejected')} disabled={actioningId === r.request_id}
                className="flex items-center gap-1 bg-red-50 text-red-600 border border-red-100 px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-50">
                <XCircle className="w-3.5 h-3.5" /> Reject
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ===== Employee panel: status of their own submitted requests =====
const MyMaintenanceRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await maintenanceRequestService.getMy();
      setRequests(res.data || []);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading || requests.length === 0) return null;

  return (
    <div className="bg-white rounded-card shadow-soft border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="text-sm font-bold text-gray-900">Your Maintenance Requests</h2>
      </div>
      <div className="divide-y divide-gray-50">
        {requests.map((r) => (
          <div key={r.request_id} className="px-5 py-3 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm font-semibold text-gray-800">{r.asset_name}</p>
              <p className="text-xs text-gray-500">{r.reason}</p>
              <p className="text-xs text-gray-400 mt-0.5">Estimated: PKR {r.estimated_cost.toLocaleString()}</p>
              {r.admin_comments && <p className="text-xs text-gray-500 italic mt-0.5">Admin note: {r.admin_comments}</p>}
            </div>
            <StatusBadge status={r.status} />
          </div>
        ))}
      </div>
    </div>
  );
};

// ===== New Asset Request Modal — employee demands a brand-new asset with name + reason =====
const NewAssetRequestModal = ({ onClose, onDone }) => {
  const [assetName, setAssetName] = useState('');
  const [category, setCategory] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!assetName.trim()) return setError('Please enter what asset you need.');
    if (!reason.trim()) return setError('Please explain why you need it.');

    setSubmitting(true);
    try {
      await newAssetRequestService.create({
        asset_name: assetName.trim(),
        category: category || null,
        reason: reason.trim(),
      });
      onDone();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Request New Asset</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="px-4 py-2.5 rounded-control bg-red-50 text-red-700 text-sm">{error}</div>}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">What do you need? *</label>
            <input type="text" value={assetName} onChange={(e) => setAssetName(e.target.value)}
              placeholder="e.g. Laptop"
              className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light">
              <option value="">Select category (optional)</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Reason *</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              placeholder="Why do you need this asset?"
              className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light" />
          </div>
          <p className="text-xs text-gray-400">Your request will be sent to admin for approval.</p>
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 rounded-control text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={submitting}
              className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark text-white px-4 py-2.5 rounded-control text-sm font-medium disabled:opacity-60">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Submit Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===== Admin panel: pending new-asset requests =====
const AdminNewAssetRequests = ({ onActionDone }) => {
  const { confirmDialog } = useNotification();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await newAssetRequestService.getAll('Pending');
      setRequests(res.data || []);
    } catch (err) {
      // silent — this panel is non-critical, don't block the page
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDecision = async (requestId, decision) => {
    const confirmed = await confirmDialog(
      decision === 'Approved' ? 'Approve this new asset request?' : 'Reject this new asset request?',
      { title: decision === 'Approved' ? 'Approve Request' : 'Reject Request', confirmLabel: decision === 'Approved' ? 'Approve' : 'Reject', danger: decision === 'Rejected' }
    );
    if (!confirmed) return;
    setActioningId(requestId);
    try {
      if (decision === 'Approved') {
        await newAssetRequestService.approve(requestId);
      } else {
        await newAssetRequestService.reject(requestId);
      }
      await load();
      onActionDone && onActionDone();
    } catch (err) {
      alert(err.message);
    } finally {
      setActioningId('');
    }
  };

  if (loading || requests.length === 0) return null;

  return (
    <div className="bg-white rounded-card shadow-soft border border-blue-100 overflow-hidden">
      <div className="px-5 py-4 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
        <Package className="w-4 h-4 text-blue-600" />
        <h2 className="text-sm font-bold text-blue-800">New Asset Requests ({requests.length})</h2>
      </div>
      <div className="divide-y divide-gray-50">
        {requests.map((r) => (
          <div key={r.request_id} className="px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm font-bold text-gray-900">{r.asset_name} {r.category && <span className="text-xs text-gray-400 font-normal">({r.category})</span>}</p>
              <p className="text-xs text-gray-500 mt-0.5">Requested by {r.employee_name}</p>
              <p className="text-xs text-gray-600 mt-1">{r.reason}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => handleDecision(r.request_id, 'Approved')} disabled={actioningId === r.request_id}
                className="flex items-center gap-1 bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-50">
                <CheckCircle2 className="w-3.5 h-3.5" /> Approve
              </button>
              <button onClick={() => handleDecision(r.request_id, 'Rejected')} disabled={actioningId === r.request_id}
                className="flex items-center gap-1 bg-red-50 text-red-600 border border-red-100 px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-50">
                <XCircle className="w-3.5 h-3.5" /> Reject
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ===== Employee panel: status of their own new-asset requests =====
const MyNewAssetRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await newAssetRequestService.getMy();
      setRequests(res.data || []);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading || requests.length === 0) return null;

  return (
    <div className="bg-white rounded-card shadow-soft border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="text-sm font-bold text-gray-900">Your New Asset Requests</h2>
      </div>
      <div className="divide-y divide-gray-50">
        {requests.map((r) => (
          <div key={r.request_id} className="px-5 py-3 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm font-semibold text-gray-800">{r.asset_name} {r.category && <span className="text-xs text-gray-400 font-normal">({r.category})</span>}</p>
              <p className="text-xs text-gray-500">{r.reason}</p>
              {r.admin_comments && <p className="text-xs text-gray-500 italic mt-0.5">Admin note: {r.admin_comments}</p>}
            </div>
            <StatusBadge status={r.status} />
          </div>
        ))}
      </div>
    </div>
  );
};

// ===== Asset History Modal (New Feature) =====
const AssetHistoryModal = ({ asset, onClose }) => {
  const history = [...(asset.assignment_history || [])].reverse(); // most recent first
  const maintenance = [...(asset.maintenance_history || [])].reverse();

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{asset.name}</h2>
            <p className="text-xs text-gray-400 font-mono mt-0.5">{asset.asset_id} &bull; {asset.make} {asset.model}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-surface-subtle rounded-control p-3">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Currently With</p>
              <p className="text-sm font-semibold text-gray-800 mt-0.5">{asset.assigned_to_name || 'Unassigned'}</p>
            </div>
            <div className="bg-surface-subtle rounded-control p-3">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Condition</p>
              <p className="text-sm font-semibold text-gray-800 mt-0.5">{asset.condition}</p>
            </div>
            <div className="bg-surface-subtle rounded-control p-3">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Purchase Price</p>
              <p className="text-sm font-semibold text-gray-800 mt-0.5">PKR {asset.purchase_cost?.toLocaleString()}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">on {asset.purchase_date}</p>
            </div>
            <div className="bg-surface-subtle rounded-control p-3">
              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Depreciated Value</p>
              <p className="text-sm font-semibold text-primary mt-0.5">PKR {asset.depreciated_value?.toLocaleString() ?? asset.purchase_cost?.toLocaleString()}</p>
            </div>
          </div>

          {/* Assignment History */}
          <div>
            <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <History className="w-3.5 h-3.5" /> Assignment History
            </h3>
            {history.length === 0 ? (
              <p className="text-sm text-gray-400">No assignment history yet.</p>
            ) : (
              <div className="space-y-3">
                {history.map((h, idx) => (
                  <div key={idx} className="flex items-start gap-3 border-b border-gray-50 pb-3 last:border-0">
                    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <Calendar className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{h.employee_name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {h.assignment_date} {h.return_date ? ('→ ' + h.return_date) : '→ Present'}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Condition at handover: {h.condition_on_assignment}
                        {h.condition_on_return ? (' · Returned as: ' + h.condition_on_return) : ''}
                      </p>
                      {h.notes && <p className="text-xs text-gray-400 italic mt-0.5">{h.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Maintenance History */}
          {maintenance.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <Wrench className="w-3.5 h-3.5" /> Maintenance History
              </h3>
              <div className="space-y-2">
                {maintenance.map((m, idx) => (
                  <div key={idx} className="flex items-center justify-between border-b border-gray-50 pb-2 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{m.issue}</p>
                      <p className="text-xs text-gray-400">{m.vendor} &bull; {m.date}</p>
                    </div>
                    <p className="text-xs text-gray-500">PKR {m.cost?.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ===== Main Assets Page (Enhanced for SRS) =====
const Assets = () => {
  const { user } = useAuth();
  const { confirmDialog } = useNotification();
  const isAdmin = user && ['super_admin', 'hr_manager', 'finance_manager'].includes(user.role);

  const [assets, setAssets] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [assigningAsset, setAssigningAsset] = useState(null);
  const [returningAsset, setReturningAsset] = useState(null);
  const [maintainingAsset, setMaintainingAsset] = useState(null);
  const [historyAsset, setHistoryAsset] = useState(null);
  const [disposingAsset, setDisposingAsset] = useState(null);
  const [requestingMaintenanceAsset, setRequestingMaintenanceAsset] = useState(null);
  const [showGeneralRequestModal, setShowGeneralRequestModal] = useState(false);
  const [showNewAssetRequestModal, setShowNewAssetRequestModal] = useState(false);
  const [actionId, setActionId] = useState('');

  const loadAssets = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await assetService.getAssets(statusFilter || undefined, categoryFilter || undefined);
      let data = res.data || [];
      
      // SRS RULE: If not admin, only show assets assigned to the current employee
      if (!isAdmin && user.employee_id) {
        data = data.filter(a => a.assigned_to === user.employee_id);
      }
      setAssets(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, categoryFilter, user]);

  // Check if warranty expires within 30 days (SRS AST-07)
  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 30;
  };

  const handleCompleteMaintenance = async (assetId) => {
    const confirmed = await confirmDialog('Mark maintenance as complete? Asset will be set back to Available.', { title: 'Complete Maintenance', confirmLabel: 'Complete', danger: false });
    if (!confirmed) return;
    setActionId(assetId);
    try {
      await assetService.completeMaintenance(assetId);
      loadAssets();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionId('');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 tracking-tight">Assets Inventory</h1>
          <p className="text-sm text-gray-500 mt-0.5">{isAdmin ? 'Track company hardware and maintenance' : 'Your assigned hardware'}</p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowNewModal(true)}
            className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark text-white px-4 py-2.5 rounded-control text-sm font-medium transition-colors shrink-0">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Register Asset</span>
          </button>
        )}
       {!isAdmin && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowNewAssetRequestModal(true)}
              className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark text-white px-4 py-2.5 rounded-control text-sm font-medium transition-colors shrink-0">
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Request New Asset</span>
            </button>
            <button onClick={() => setShowGeneralRequestModal(true)}
              className="flex items-center gap-1.5 bg-primary hover:bg-blue-600 text-white px-4 py-2.5 rounded-control text-sm font-medium transition-colors shrink-0">
              <Wrench className="w-4 h-4" />
              <span className="hidden sm:inline">Request Maintenance</span>
            </button>
          </div>
        )}
      </div>

      {isAdmin && <AdminNewAssetRequests onActionDone={loadAssets} />}
      {isAdmin && <AdminMaintenanceRequests onActionDone={loadAssets} />}
      {!isAdmin && <MyNewAssetRequests />}
      {!isAdmin && <MyMaintenanceRequests />}
      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light">
          <option value="">All Statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 py-2 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light">
          <option value="">All Categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {error && (
        <div className="mb-4 px-4 py-2.5 rounded-control bg-red-50 text-red-700 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      ) : assets.length === 0 ? (
        <div className="bg-white rounded-card shadow-soft border border-gray-100 p-12 text-center">
          <Package className="w-10 h-10 text-gray-300 mx-auto mb-3 opacity-20" />
          <p className="text-sm text-gray-500">No assets found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Desktop Table View */}
          <div className="hidden md:block bg-white rounded-card shadow-soft border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-xs font-black text-gray-400 uppercase tracking-widest">
                  <th className="px-6 py-4">Asset</th>
                  <th className="px-6 py-4">Category</th>
                  <th className="px-6 py-4">Condition</th>
                  <th className="px-6 py-4">Status</th>
                  {isAdmin && <th className="px-6 py-4">Financial Value</th>}
                  <th className="px-6 py-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {assets.map((a) => (
                  <tr key={a.id} className="hover:bg-surface-subtle transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div>
                          {isAdmin ? (
                            <button
                              onClick={() => setHistoryAsset(a)}
                              className="font-bold text-gray-900 hover:text-primary hover:underline text-left"
                              title="View asset history"
                            >
                              {a.name}
                            </button>
                          ) : (
                            <span className="font-bold text-gray-900">{a.name}</span>
                          )}
                          <p className="text-[10px] text-gray-400 uppercase tracking-tighter font-mono">{a.asset_id} &bull; {a.make}</p>
                        </div>
                        {isExpiringSoon(a.warranty_expiry_date) && (
                          <span className="p-1 bg-red-50 text-red-600 rounded-full animate-pulse" title="Warranty Expiring Soon">
                            <AlertTriangle className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{a.category}</td>
                    <td className="px-6 py-4 text-gray-600">{a.condition}</td>
                    <td className="px-6 py-4"><StatusBadge status={a.status} /></td>
                    {isAdmin && (
                      <td className="px-6 py-4">
                        <p className="text-[10px] text-gray-400 uppercase">Cost: {a.purchase_cost?.toLocaleString()} PKR</p>
                        <p className="text-xs font-bold text-primary flex items-center gap-1">
                          <TrendingDown className="w-3 h-3" /> 
                          {a.depreciated_value ? a.depreciated_value.toLocaleString() : a.purchase_cost?.toLocaleString()}
                        </p>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {isAdmin ? (
                          <>
                            <button onClick={() => setEditingAsset(a)} className="p-2 text-gray-400 hover:text-primary-light"><Pencil className="w-4 h-4" /></button>
                            {a.status === 'Available' && <button onClick={() => setAssigningAsset(a)} className="text-xs font-bold text-primary hover:underline">Assign</button>}
                            {a.status === 'Assigned' && <button onClick={() => setReturningAsset(a)} className="text-xs font-bold text-amber-500 hover:underline">Return</button>}
                            {a.status === 'Under Maintenance' && (
                              <button onClick={() => handleCompleteMaintenance(a.asset_id)} disabled={actionId === a.asset_id}
                                className="text-xs font-bold text-green-600 hover:underline disabled:opacity-50">
                                {actionId === a.asset_id ? 'Marking...' : 'Mark Available'}
                              </button>
                            )}
                            <button onClick={() => setMaintainingAsset(a)} className="p-2 text-gray-400 hover:text-blue-500"><Wrench className="w-4 h-4" /></button>
                            {a.status !== 'Disposed' && (
                              <button onClick={() => setDisposingAsset(a)} className="p-2 text-gray-400 hover:text-red-500" title="Delete Asset">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </>
                        ) : (
                          a.status !== 'Under Maintenance' && a.status !== 'Disposed' && (
                            <button onClick={() => setRequestingMaintenanceAsset(a)} className="text-xs font-bold text-blue-500 hover:underline flex items-center gap-1">
                              <Wrench className="w-3.5 h-3.5" /> Request Maintenance
                            </button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {assets.map((a) => (
              <div key={a.id} className="bg-white rounded-card shadow-soft border border-gray-100 p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {isAdmin ? (
                      <button
                        onClick={() => setHistoryAsset(a)}
                        className="font-bold text-gray-900 hover:text-primary hover:underline text-left"
                        title="View asset history"
                      >
                        {a.name}
                      </button>
                    ) : (
                      <span className="font-bold text-gray-900">{a.name}</span>
                    )}
                    {isExpiringSoon(a.warranty_expiry_date) && <AlertTriangle className="w-4 h-4 text-red-500 animate-pulse" />}
                  </div>
                  <StatusBadge status={a.status} />
                </div>
                <div className="mt-2 text-xs text-gray-500 flex justify-between uppercase font-bold tracking-widest">
                  <span>{a.category}</span>
                  <span>{a.condition}</span>
                </div>
                {isAdmin ? (
                  <div className="mt-3 pt-3 border-t border-gray-50 flex items-center gap-4">
                    <button onClick={() => setEditingAsset(a)} className="text-xs font-bold text-primary">Edit</button>
                    {a.status === 'Available' && <button onClick={() => setAssigningAsset(a)} className="text-xs font-bold text-primary">Assign</button>}
                    {a.status === 'Assigned' && <button onClick={() => setReturningAsset(a)} className="text-xs font-bold text-amber-500">Return</button>}
                    {a.status === 'Under Maintenance' && (
                      <button onClick={() => handleCompleteMaintenance(a.asset_id)} disabled={actionId === a.asset_id} className="text-xs font-bold text-green-600 disabled:opacity-50">
                        {actionId === a.asset_id ? 'Marking...' : 'Mark Available'}
                      </button>
                    )}
                    {a.status !== 'Disposed' && (
                      <button onClick={() => setDisposingAsset(a)} className="text-xs font-bold text-red-500">Delete</button>
                    )}
                  </div>
                ) : (
                  a.status !== 'Under Maintenance' && a.status !== 'Disposed' && (
                    <div className="mt-3 pt-3 border-t border-gray-50">
                      <button onClick={() => setRequestingMaintenanceAsset(a)} className="text-xs font-bold text-blue-500 flex items-center gap-1">
                        <Wrench className="w-3.5 h-3.5" /> Request Maintenance
                      </button>
                    </div>
                  )
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {isAdmin && historyAsset && <AssetHistoryModal asset={historyAsset} onClose={() => setHistoryAsset(null)} />}
      {requestingMaintenanceAsset && (
        <MaintenanceRequestModal
          asset={requestingMaintenanceAsset}
          assets={assets}
          onClose={() => setRequestingMaintenanceAsset(null)}
          onDone={() => { setRequestingMaintenanceAsset(null); loadAssets(); }}
        />
      )}
      {showGeneralRequestModal && (
        <MaintenanceRequestModal
          asset={null}
          assets={assets}
          onClose={() => setShowGeneralRequestModal(false)}
          onDone={() => { setShowGeneralRequestModal(false); loadAssets(); }}
        />
      )}
      {showNewAssetRequestModal && (
        <NewAssetRequestModal
          onClose={() => setShowNewAssetRequestModal(false)}
          onDone={() => { setShowNewAssetRequestModal(false); }}
        />
      )}

      {/* Modals - Only if Admin */}
      {isAdmin && (
        <>
          {showNewModal && <AssetModal onClose={() => setShowNewModal(false)} onDone={() => { setShowNewModal(false); loadAssets(); }} />}
          {editingAsset && <AssetModal editingAsset={editingAsset} onClose={() => setEditingAsset(null)} onDone={() => { setEditingAsset(null); loadAssets(); }} />}
          {assigningAsset && <AssignModal asset={assigningAsset} onClose={() => setAssigningAsset(null)} onDone={() => { setAssigningAsset(null); loadAssets(); }} />}
          {returningAsset && <ReturnModal asset={returningAsset} onClose={() => setReturningAsset(null)} onDone={() => { setReturningAsset(null); loadAssets(); }} />}
          {maintainingAsset && <MaintenanceModal asset={maintainingAsset} onClose={() => setMaintainingAsset(null)} onDone={() => { setMaintainingAsset(null); loadAssets(); }} />}
          {disposingAsset && <DisposeModal asset={disposingAsset} onClose={() => setDisposingAsset(null)} onDone={() => { setDisposingAsset(null); loadAssets(); }} />}
        </>
      )}
    </div>
  );
};

export default Assets;